/**
 * ProfileHeader - Profile photo (with upload overlay), user info, sign out and role badge
 */

import { RefObject, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2 } from 'lucide-react';
import { User } from '@/types';

interface ProfileHeaderProps {
  user: User;
  uploadingPhoto: boolean;
  fileInputRef: RefObject<HTMLInputElement>;
  onPhotoSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onSignOut: () => void;
}

export default function ProfileHeader({ user, uploadingPhoto, fileInputRef, onPhotoSelect, onSignOut }: ProfileHeaderProps) {
  const { t } = useTranslation();

  // Get initials from name
  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return name.substring(0, 2).toUpperCase();
    }
  };

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          {/* Profile Photo with Upload */}
          <div className="relative group">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="w-20 h-20 rounded-full ring-1 ring-hairline object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-surface-3 flex items-center justify-center text-2xl font-semibold text-ink-muted ring-1 ring-hairline">
                {user.displayName ? getInitials(user.displayName) : user.email ? getInitials(user.email) : '?'}
              </div>
            )}
            {/* Upload Button Overlay */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute inset-0 rounded-full bg-canvas/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer disabled:cursor-wait"
              title={t('profilePage.changePhoto')}
            >
              {uploadingPhoto
                ? <Loader2 className="w-6 h-6 text-ink animate-spin" aria-hidden="true" />
                : <Camera className="w-6 h-6 text-ink" aria-hidden="true" />}
            </button>
            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPhotoSelect}
              className="hidden"
              disabled={uploadingPhoto}
            />
          </div>

          <div>
            <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">{user.displayName || t('profilePage.userFallback')}</h1>
            <p className="text-sm text-ink-muted">{user.email}</p>
            {user.phoneNumber && (
              <p className="text-sm text-ink-muted">{user.phoneNumber}</p>
            )}
            <p className="inline-flex items-center gap-1.5 text-xs text-ink-subtle mt-1">
              <Camera className="w-3 h-3" aria-hidden="true" /> {t('profilePage.hoverToChange')}
            </p>
          </div>
        </div>
        <button
          onClick={() => onSignOut()}
          className="px-4 py-2 text-sm text-ink-muted hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
        >
          {t('profilePage.signOut')}
        </button>
      </div>

      {/* Role Badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-ink-muted">{t('profilePage.roleLabel')}</span>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
          user.role === 'super_admin' ? 'bg-info/10 text-info border-info/30' :
          user.role === 'admin' ? 'bg-info/10 text-info border-info/30' :
          user.role === 'barber' ? 'bg-success/10 text-success border-success/30' :
          user.role === 'client' ? 'bg-surface-2 text-ink-muted border-hairline' :
          'bg-accent/10 text-accent border-accent/30'
        }`}>
          {user.role === 'super_admin' ? t('profilePage.roles.superAdmin') :
           user.role === 'admin' ? t('profilePage.roles.admin') :
           user.role === 'barber' ? t('profilePage.roles.barber') :
           user.role === 'client' ? t('profilePage.roles.client') :
           t('profilePage.roles.guest')}
        </span>
      </div>
    </div>
  );
}
